<template>
  <div class="accordion">
    <div class="accordion-header" @click="toggleAccordion" :class="{ active: isOpen }">
      <slot name="header"></slot>
      <ChevronRightIcon
        class="w-5 h-5 transition-transform duration-200"
        :class="{ 'rotate-90': isOpen }"
      />
    </div>
    <transition
      name="accordion"
      @enter="startTransition"
      @after-enter="endTransition"
      @before-leave="startTransition"
      @after-leave="endTransition"
    >
      <div v-show="isOpen" class="accordion-content">
        <slot name="content"></slot>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { ChevronRightIcon } from '@heroicons/vue/24/solid';
import { defineProps, ref, watch } from 'vue';

const props = defineProps({
  defaultOpen: {
    type: Boolean,
    default: false,
  },
  open: { 
    type: Boolean, 
    default: undefined 
  }
});
const emit = defineEmits(["toggle"]);
const isControlled = typeof props.open === 'boolean';
const isOpen = ref(isControlled ? props.open : props.defaultOpen);
watch(
  () => props.open,
  (val) => {
    if (isControlled) {
      isOpen.value = val;
    }
  }
);
const toggleAccordion = () => {
  emit("toggle");
  if (!isControlled) {
    isOpen.value = !isOpen.value;
  }
};

const startTransition = (el: Element) => {
  const element = el as HTMLElement;
  element.style.height = 'auto';
  const height = element.scrollHeight;
  element.style.height = '0px';
  // Trigger reflow
  element.offsetHeight;
  element.style.height = `${height}px`;
};

const endTransition = (el: Element) => {
  const element = el as HTMLElement;
  element.style.height = '';
};
</script>

<style scoped lang="scss">
.accordion {
  @apply border border-line rounded-md mb-2;
}

.accordion-header {
  @apply flex justify-between items-center p-4 cursor-pointer bg-surface-2 hover:bg-surface-3 transition-colors duration-200;
}

.accordion-header.active {
  @apply bg-surface-3;
}

.accordion-content {
  @apply p-4 bg-surface overflow-hidden;
}

.accordion-enter-active,
.accordion-leave-active {
  transition: height 0.3s ease;
}

.accordion-enter-from,
.accordion-leave-to {
  height: 0;
}
</style>
